// src/pages/CattlePage.js
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAllCattle, addCattle, updateCattle, deleteCattle } from '../utils/cattleService';
import { exportToExcel } from '../utils/excelUtils';
import CattleForm from '../components/CattleForm';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const PAGE_SIZE = 50;

export default function CattlePage() {
  const { currentUser, canAdd, canEdit, isPaid, incrementAddCount, incrementEditCount } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    const data = await getAllCattle(currentUser.uid);
    setRecords(data);
    setLoading(false);
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const filtered = records.filter(r =>
    !filter ||
    String(r.tagNumber).includes(filter) ||
    (r.breed || '').toLowerCase().includes(filter.toLowerCase()) ||
    (r.gender || '').toLowerCase().includes(filter.toLowerCase()) ||
    (r.color || '').toLowerCase().includes(filter.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  async function handleAdd(data) {
    if (!canAdd) { navigate('/upgrade'); return; }
    await addCattle(currentUser.uid, data);
    await incrementAddCount();
    toast.success('Record added!');
    load();
  }

  async function handleEdit(data) {
    if (!canEdit) { navigate('/upgrade'); return; }
    await updateCattle(currentUser.uid, editRecord.id, data);
    await incrementEditCount();
    toast.success('Record updated!');
    load();
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this record?')) return;
    await deleteCattle(currentUser.uid, id);
    toast.success('Record deleted');
    load();
  }

  function startAdd() {
    if (!canAdd) { toast.error('Free plan limit reached! Upgrade to add more.'); navigate('/upgrade'); return; }
    setEditRecord(null);
    setShowForm(true);
  }

  function startEdit(record) {
    if (!canEdit) { toast.error('Free plan limit reached! Upgrade to edit more.'); navigate('/upgrade'); return; }
    setEditRecord(record);
    setShowForm(true);
  }

  return (
    <div className="page-container">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1b5e20', flex: 1 }}>📋 Cattle Records</h1>
        <div className="search-box" style={{ width: 260 }}>
          <span className="icon">🔍</span>
          <input value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }} placeholder="Filter by tag, breed, gender..." />
        </div>
        <button className="btn btn-primary" onClick={startAdd}>+ Add Record</button>
        <button className="btn btn-secondary" onClick={() => exportToExcel(records)}>📤 Export Excel</button>
      </div>

      <div style={{ marginBottom: 10, color: '#6b7280', fontSize: 13 }}>
        Showing {paginated.length} of {filtered.length} records
        {!isPaid && <span style={{ color: '#d97706' }}> · Free plan</span>}
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : paginated.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: 48 }}>🐄</div>
          <p>No records found. Import an Excel file or add records manually.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Tag Number</th>
                <th>Entry Date</th>
                <th>Breed</th>
                <th>Age</th>
                <th>Gender</th>
                <th>Color</th>
                <th>Status</th>
                <th>Exit Date</th>
                <th>Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((r, i) => (
                <tr key={r.id}>
                  <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                  <td><strong>{r.tagNumber}</strong></td>
                  <td>{r.dateOfEntry}</td>
                  <td>{r.breed}</td>
                  <td>{r.age} yr</td>
                  <td>
                    <span className={`badge ${r.gender === 'MALE' ? 'badge-male' : 'badge-female'}`}>
                      {r.gender}
                    </span>
                  </td>
                  <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.color}</td>
                  <td>
                    <span className={`badge ${r.dateOfExit ? 'badge-exited' : 'badge-alive'}`}>
                      {r.dateOfExit ? 'Exited' : 'Alive'}
                    </span>
                  </td>
                  <td>{r.dateOfExit || '—'}</td>
                  <td>{r.exitReason || '—'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => startEdit(r)}>✏️ Edit</button>
                      <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => handleDelete(r.id)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button className="page-btn" onClick={() => setPage(1)} disabled={page === 1}>«</button>
          <button className="page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
            return <button key={p} className={`page-btn ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>;
          })}
          <button className="page-btn" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</button>
          <button className="page-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
          <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page}/{totalPages}</span>
        </div>
      )}

      {showForm && (
        <CattleForm
          title={editRecord ? '✏️ Edit Cattle Record' : '➕ Add New Cattle Record'}
          initial={editRecord || {}}
          onSave={editRecord ? handleEdit : handleAdd}
          onClose={() => { setShowForm(false); setEditRecord(null); }}
        />
      )}
    </div>
  );
}
